<template>
  <div class="coverage-map">
    <div class="map-wrapper" :style="{ aspectRatio: aspectRatioStyle, maxHeight: wrapperMaxHeightStyle }" ref="wrapper">
      <div ref="map" class="maplibre-container"></div>
      <div v-if="showGlobeNotice" class="globe-notice" role="status">
        3D globe requires WebGL2 support. Rendering a flat map instead. Try another browser or enable hardware acceleration.
      </div>
    </div>
  </div>
  
</template>

<script>
import maplibregl from 'maplibre-gl';

export default {
  name: 'MapLibreCoverageMap',
  props: {
    // Preferred structured input: { iso3: string[], names: string[] }
    selectedCountriesDict: {
      type: Object,
      required: false,
      default: null,
    },
    // Ocean names or aliases to highlight, e.g. 'Atlantic Ocean', 'Pacific', 'INDIAN', 'ALL'
    selectedOceans: {
      type: Array,
      required: false,
      default: () => [],
    },
    aspectRatio: {
      type: [Number, String],
      required: false,
      default: 2,
    },
    // Extra bottom margin in pixels to avoid touching the viewport bottom
    bottomSafeMargin: {
      type: Number,
      required: false,
      default: 32,
      },
    dataMode: {
        type: String,
        default: 'external',
        validator: (val) => ['auto', 'external'].includes(val)
    },
  },
  data() {
    return {
      map: null,
      countriesSourceLoaded: false,
      oceansSourceLoaded: false,
      popup: null,
      wrapperMaxHeightPx: 0,
      bottomSafeMarginPx: this.bottomSafeMargin,
      // resizeObserver: null,
      // // Docs example style URL
      // styleUrl: 'https://demotiles.maplibre.org/style.json',
      supportsWebGL2: true,
      showGlobeNotice: false,
      // Selection and paint colors (targeting base map if possible)
      baseColor: '#e6e6e6',
      baseWater: '#d9edf7',
      selectedColor: '#1e90ff',
      selectionSupportedOnBase: false,
      triedApplySelection: false,
      // caches collected from sources when loaded
      allCountryIso3: [],
      allOceanNames: [],
      countryNameByIso3: {},
      // Indexes built from source for matching
      nameToIso3FromSource: {},
      exactNameToIso3FromSource: {},
    };
  },
  mounted() {
    // Detect WebGL2 support required for globe projection
    this.supportsWebGL2 = this.hasWebGL2();
    this.showGlobeNotice = !this.supportsWebGL2;
    this.initMap();
    try {
      window.addEventListener('resize', this.recalcWrapperMaxHeight, { passive: true });
      window.addEventListener('scroll', this.recalcWrapperMaxHeight, { passive: true });
      this.$nextTick(() => this.recalcWrapperMaxHeight());
    } catch { /* ignore */ }
  },
  beforeUnmount() {
    try { this.resizeObserver && this.resizeObserver.disconnect(); } catch { /* ignore */ }
    try {
      window.removeEventListener('resize', this.recalcWrapperMaxHeight);
      window.removeEventListener('scroll', this.recalcWrapperMaxHeight);
    } catch { /* ignore */ }
    try { if (this.popup) { this.popup.remove(); this.popup = null; } } catch { /* ignore */ }
    try { if (this.map) { this.map.remove(); this.map = null; } } catch { /* ignore */ }
  },
  methods: {
    async initMap() {
      // Show the official MapLibre docs example map
      try {
        if (!maplibregl) {
          console.error('MapLibre GL JS not loaded');
          return;
        }
        this.map = new maplibregl.Map({
          container: this.$refs.map,
          style: this.buildStyle(),
          projection: this.supportsWebGL2 ? { name: 'globe' } : { name: 'mercator' },
          attributionControl: false,
          center: [0, 0],
          zoom: 1,
          antialias: true,
        });
        this.map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
        this.map.addControl(new maplibregl.GlobeControl(), 'top-right');
        this.map.addControl(new maplibregl.AttributionControl({
          compact: true,
          customAttribution: 'MapLibre'
        }), 'bottom-right');
        // Apply atmosphere (fog) and attempt selection coloring on base map once the style is ready
        this.map.on('style.load', async () => {
          try {
            if (this.supportsWebGL2) {
              this.map.setProjection({ type: 'globe' });
              this.map.setFog({
                color: 'rgb(186, 210, 235)',
                'high-color': 'rgb(36, 92, 237)',
                'space-color': 'rgb(11, 11, 25)',
                'star-intensity': 0.0,
              });
            }
          } catch { /* ignore */ }
          // Apply selection once the style is ready
          this.applyFeatureStateSelection();
          // Re-apply selection once the GeoJSON source has loaded
          try {
            this.map.on('sourcedata', async (e) => {
              if (e && e.sourceId === 'countries' && e.isSourceLoaded) {
                this.countriesSourceLoaded = true;
                await this.analyzeCountriesSource();
                this.applyFeatureStateSelection();
              }
              if (e && e.sourceId === 'oceans' && e.isSourceLoaded) {
                this.oceansSourceLoaded = true;
                await this.analyzeOceansSource();
                this.applyFeatureStateSelection();
              }
            });
          } catch { /* ignore */ }
        });
      } catch(e) {
        // if map init fails, abort quietly
        console.error('MapLibre init failed:', e);
        return;
      }

      // Observe wrapper to keep map sized correctly and emit height
      try {
        const wrapper = this.$el.querySelector('.map-wrapper');
        if (wrapper && window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            try { this.map && this.map.resize(); } catch { /* ignore */ }
            try { const h = wrapper.clientHeight || 0; this.$emit('resized', h); } catch { /* ignore */ }
          });
          this.resizeObserver.observe(wrapper);
          try { this.$emit('resized', wrapper.clientHeight || 0); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    },
    buildStyle() {
      return {
        version: 8,
        name: 'Globe Countries',
        // No glyphs: let MapLibre use local/system fonts (requires MapLibre GL JS >= 5.11.0)
        //glyphs: '',
        sources: {
          oceans: {
            type: 'geojson',
            // Natural Earth 110m ocean polygons
            data: 'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/refs/heads/master/geojson/ne_110m_geography_marine_polys.geojson',
            promoteId: 'name',
            attribution: 'Natural Earth',
          },
          countries: {
            type: 'geojson',
            // Natural Earth 10m country polygons
            data: 'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/refs/heads/master/geojson/ne_10m_admin_0_countries.geojson',
            promoteId: 'ADM0_A3',
            attribution: 'Natural Earth',
          },
          // Label points generated at runtime from countries LABEL_X/LABEL_Y
          'country-label-points': {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          },
        },
        layers: [
          { id: 'background', type: 'background', paint: { 'background-color': this.baseWater } },
          // Oceans selection overlay (transparent when not selected)
          {
            id: 'oceans-fill',
            type: 'fill',
            source: 'oceans',
            paint: {
              'fill-color': ['case', ['boolean', ['feature-state', 'selected'], false], this.selectedColor, 'rgba(0,0,0,0)'],
              'fill-opacity': 1,
            },
          },
          { id: 'oceans-outline', type: 'line', source: 'oceans', paint: { 'line-color': 'rgba(0,0,0,0)', 'line-width': 0.0 } },
          {
            id: 'countries-fill',
            type: 'fill',
            source: 'countries',
            paint: {
              'fill-color': ['case', ['boolean', ['feature-state', 'selected'], false], this.selectedColor, this.baseColor],
              'fill-opacity': 1,
            },
          },
          { id: 'countries-outline', type: 'line', source: 'countries', paint: { 'line-color': '#ffffff', 'line-width': 0.5 } },
          // Ocean labels (always visible)
          {
            id: 'ocean-labels',
            type: 'symbol',
            source: 'oceans',
            layout: {
              'text-field': ['get', 'name'],
              'text-font': ['Roboto','system-ui'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 3, 13, 5, 18],
              'text-transform': 'uppercase',
              'text-allow-overlap': false,
            },
            paint: {
              'text-color': '#1e90ff',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.0,
            },
          },
          // Country labels from generated label points (LABEL_X/LABEL_Y), ABBREV at low zoom -> NAME at higher zoom
          {
            id: 'countries-label',
            type: 'symbol',
            source: 'country-label-points',
            minzoom: 2,
            layout: {
              'text-field': {
                stops: [ [2, '{ABBREV}'], [4, '{NAME}'] ],
              },
              'text-font': ['Roboto','system-ui'],
              'text-size': { stops: [ [2, 10], [4, 12], [6, 16] ] },
              'text-max-width': 10,
              'text-transform': { stops: [ [0, 'uppercase'], [2, 'none'] ] },
              'visibility': 'visible',
            },
            paint: {
              'text-color': 'rgba(8, 37, 77, 1)',
              'text-halo-color': 'rgba(255, 255, 255, 1)',
              'text-halo-width': { stops: [ [2, 1.0], [6, 1.6] ] },
              'text-halo-blur': { stops: [ [2, 0.2], [6, 0] ] },
            },
          },
        ],
        projection: { type: 'globe' },
      };
    },
    applyFeatureStateSelection() {
      try {
        if (!this.map) return;
        // Countries
        if (this.countriesSourceLoaded) {
          // First, clear any previously selected codes (including ones no longer considered valid)
          try {
            if (!this._prevSelected) this._prevSelected = [];
            for (const code of this._prevSelected) {
              try { this.map.setFeatureState({ source: 'countries', id: code }, { selected: false }); } catch { /* ignore */ }
            }
          } catch { /* ignore */ }
          // Compute from the structured dict only (iso3 + names)
          const next = (this.selectedCountriesDict && Object.keys(this.selectedCountriesDict).length)
            ? this._computeSelectionFromDict(this.selectedCountriesDict)
            : [];
          const valid = (this.allCountryIso3 && this.allCountryIso3.length)
            ? next.filter((c) => this.allCountryIso3.includes(c))
            : next;
          for (const code of valid) {
            try { this.map.setFeatureState({ source: 'countries', id: code }, { selected: true }); } catch { /* ignore */ }
          }
          this._prevSelected = valid;
        }
        // Oceans
        if (this.oceansSourceLoaded) {
          if (!this._prevSelectedOceans) this._prevSelectedOceans = [];
          for (const name of this._prevSelectedOceans) {
            try { this.map.setFeatureState({ source: 'oceans', id: name }, { selected: false }); } catch { /* ignore */ }
          }
          const nextO = this.normalizeOceans(this.selectedOceans || []);
          for (const name of nextO) {
            try { this.map.setFeatureState({ source: 'oceans', id: name }, { selected: true }); } catch { /* ignore */ }
          }
          this._prevSelectedOceans = nextO;
        }
      } catch { /* ignore */ }
    },
    // Read a GeoJSON source's features via the public async API. src._data no longer
    // holds the FeatureCollection in MapLibre 5.x (it wraps as {url}/{geojson}), and for
    // URL sources the data only ever exists in the worker — getData() fetches it from there.
    async _getSourceFeatures(sourceId) {
      try {
        const src = this.map && this.map.getSource(sourceId);
        if (!src) return null;
        if (typeof src.getData === 'function') {
          try {
            const geo = await src.getData();
            if (geo && geo.features) return geo.features;
          } catch { /* ignore */ }
        }
        const d = src._data;
        if (d && d.features) return d.features;
        if (d && d.geojson && d.geojson.features) return d.geojson.features;
      } catch { /* ignore */ }
      return null;
    },
    async analyzeCountriesSource() {
      try {
        const data = await this._getSourceFeatures('countries');
        if (!data || !data.length) return;
        const sampleCodes = new Set();
        const nameTo3 = {};
        const exactNameTo3 = {};
        for (let i = 0; i < Math.min(20, data.length); i += 1) {
          const p = data[i].properties || {};
          if (p['ADM0_A3']) sampleCodes.add(String(p['ADM0_A3']));
          else if (p['ISO_A3']) sampleCodes.add(String(p['ISO_A3']));
        }
        // cache all country codes for validation and GLOBAL expansion (valid ISO A3)
        const validA3 = (c) => /^[A-Z]{3}$/.test(String(c || ''));
        this.allCountryIso3 = data
          .map((f) => {
            const p3 = f.properties || {};
            const raw = (p3['ADM0_A3'] ?? p3['ISO_A3'] ?? p3['ISO3'] ?? '')
              .toString().trim().toUpperCase();
            return raw;
          })
          .filter((c) => validA3(c));
        // map ISO A3 -> readable name for debugging output and name resolution
        try {
          const nameMap = {};
          for (const f of data) {
            const p2 = f.properties || {};
            const c3 = (p2['ADM0_A3'] ?? p2['ISO_A3'] ?? p2['ISO3'] ?? '')
              .toString().trim().toUpperCase();
            if (!c3) continue;
            if (!validA3(c3)) continue; // skip invalid codes like '-99'
            // Prefer the human label name used for symbols (NAME/NAME_LONG), fall back to ADMIN/name
            const labelName = String(p2.NAME || p2.NAME_LONG || p2.ADMIN || p2.name || p2.SOVEREIGNT || '').trim();
            const adminName = String(p2.ADMIN || p2.name || '').trim();
            const abbrev = String(p2.ABBREV || '').trim();

            if (labelName) nameMap[c3] = labelName; // for debug output
            // Build matching dictionaries from multiple name fields used in labels
            const namesForMatch = new Set([
              labelName,
              adminName,
              abbrev,
            ].filter(Boolean));
            for (const nm of namesForMatch) {
              const canon = this._canonName(nm);
              if (canon) nameTo3[canon] = c3;
              exactNameTo3[nm] = c3;
            }
          }
          this.countryNameByIso3 = nameMap;
          this.nameToIso3FromSource = nameTo3;
          this.exactNameToIso3FromSource = exactNameTo3;
        } catch { /* ignore */ }
        // Build label points from LABEL_X/LABEL_Y and attach to 'country-label-points' source
        try {
          const features = [];
          for (const f of data) {
            const p = f.properties || {};
            const x = Number(p.LABEL_X);
            const y = Number(p.LABEL_Y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
            const c3 = (p['ADM0_A3'] ?? p['ISO_A3'] ?? p['ISO3'] ?? '')
              .toString().trim().toUpperCase();
            const name = String(p.NAME || p.NAME_LONG || p.ADMIN || p.SOVEREIGNT || '').trim();
            const abbrev = String(p.ABBREV || '').trim() || name;
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [x, y] },
              properties: { ISO_A3: c3, NAME: name, ABBREV: abbrev },
            });
          }
          const fc = { type: 'FeatureCollection', features };
          const lbl = this.map.getSource('country-label-points');
          if (lbl && fc.features.length) {
            try { lbl.setData(fc); } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    },
    async analyzeOceansSource() {
      try {
        const data = await this._getSourceFeatures('oceans');
        if (!data || !data.length) return;
        const names = new Set();
        for (let i = 0; i < data.length; i += 1) {
          const p = data[i].properties || {};
          if (p.name) names.add(String(p.name));
        }
        this.allOceanNames = Array.from(names);
      } catch { /* ignore */ }
    },
    _canonName(s) {
      try {
        return String(s || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();
      } catch { return String(s || '').toLowerCase().trim(); }
    },
    _computeSelectionFromDict(dict) {
      try {
        if (!dict) return [];
        // GLOBAL / WORLD special case via names
        const namesArr = Array.isArray(dict.names) ? dict.names : [];
        const hasGlobal = namesArr.some((n) => /^(global|world)$/i.test(String(n || '').trim()));
        if (hasGlobal && this.allCountryIso3?.length) return Array.from(this.allCountryIso3);
        const iso3Set = new Set();
        // 1) ISO3 direct (validate against source)
        if (Array.isArray(dict.iso3)) {
          for (const c3 of dict.iso3) {
            const up3 = String(c3 || '').trim().toUpperCase();
            if (!/^[A-Z]{3}$/.test(up3)) continue;
            if (!this.allCountryIso3 || !this.allCountryIso3.length || this.allCountryIso3.includes(up3)) {
              iso3Set.add(up3);
            }
          }
        }
        // 2) Names via source mapping -> ISO3
        if (namesArr && namesArr.length) {
          for (const n of namesArr) {
            const name = String(n || '').trim();
            let a2 = null;
            // 3a) exact ADMIN/name match first
            if (name) a2 = this.exactNameToIso3FromSource[name] || null;
            // 3b) fallback to canonical match if exact not found
            if (!a2) {
              const canon = this._canonName(name);
              a2 = this.nameToIso3FromSource[canon] || null;
            }
            if (a2 && (!this.allCountryIso3 || !this.allCountryIso3.length || this.allCountryIso3.includes(a2))) iso3Set.add(a2);
          }
        }
        return Array.from(iso3Set);
      } catch { return []; }
    },
    normalizeOceans(arr) {
      try {
        const alias = {
          ARCTIC: 'Arctic Ocean', 'ARCTIC OCEAN': 'Arctic Ocean', ARC: 'Arctic Ocean',
          ATLANTIC: 'Atlantic Ocean', 'ATLANTIC OCEAN': 'Atlantic Ocean', ATL: 'Atlantic Ocean', 'NORTH ATLANTIC': 'Atlantic Ocean', 'SOUTH ATLANTIC': 'Atlantic Ocean',
          PACIFIC: 'Pacific Ocean', 'PACIFIC OCEAN': 'Pacific Ocean', PAC: 'Pacific Ocean', 'NORTH PACIFIC': 'Pacific Ocean', 'SOUTH PACIFIC': 'Pacific Ocean',
          INDIAN: 'Indian Ocean', 'INDIAN OCEAN': 'Indian Ocean', IND: 'Indian Ocean',
          SOUTHERN: 'Southern Ocean', 'SOUTHERN OCEAN': 'Southern Ocean', ANTARCTIC: 'Southern Ocean',
          ALL: 'ALL', GLOBAL: 'ALL', OCEANS: 'ALL',
        };
        const input = (arr || []).map((n) => String(n || '').trim());
        const upper = input.map((n) => n.toUpperCase());
        if ((upper.includes('ALL') || upper.includes('GLOBAL')) && this.allOceanNames && this.allOceanNames.length) {
          return Array.from(this.allOceanNames);
        }
        const mapped = upper.map((u) => alias[u] || u.replace(/\bOCEAN\b/i, (m) => m[0] + m.slice(1).toLowerCase()));
        // map canonical names if already in proper case
        return mapped.map((m) => {
          // if already canonical
          if (this.allOceanNames && this.allOceanNames.includes(m)) return m;
          // try title-case match
          const t = m.split(' ').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
          return t;
        });
      } catch { return []; }
    },
    hasWebGL2() {
      try {
        const canvas = document.createElement('canvas');
        const gl2 = canvas.getContext && (canvas.getContext('webgl2') || canvas.getContext('experimental-webgl2'));
        return !!gl2;
      } catch { return false; }
    },
    // Compute CSS aspect-ratio string
    aspectToCss(val) {
      if (typeof val === 'number') return `${val} / 1`;
      if (typeof val === 'string' && val.includes('/')) return val;
      const n = Number(val);
      return Number.isFinite(n) ? `${n} / 1` : '2 / 1';
    },
    recalcWrapperMaxHeight() {
      try {
        const wrapper = this.$refs.wrapper;
        if (!wrapper) { this.wrapperMaxHeightPx = 0; return; }
        const rect = wrapper.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        const available = Math.max(0, viewportH - rect.top - (Number(this.bottomSafeMarginPx) || 0));
        this.wrapperMaxHeightPx = available;
      } catch { this.wrapperMaxHeightPx = 0; }
    },
    async flyToCountry(nameOrIso3) {
      try {
        if (!this.map || !this.countriesSourceLoaded) return;
        const iso3 = this.resolveIso3(nameOrIso3);
        if (!iso3) return;

        const features = (await this._getSourceFeatures('countries')) || [];
        const feature = features.find((f) => {
          const p = f.properties || {};
          const c3 = (p['ADM0_A3'] ?? p['ISO_A3'] ?? p['ISO3'] ?? '').toString().trim().toUpperCase();
          return c3 === iso3;
        });

        if (feature) {
          if (!maplibregl.LngLatBounds) return;
          const bounds = new maplibregl.LngLatBounds();
          this.extendBounds(bounds, feature.geometry);
          if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, { padding: 50, maxZoom: 6 });
          }
        }
      } catch { /* ignore */ }
    },
    resolveIso3(input) {
      const name = String(input || '').trim();
      if (!name) return null;
      const up = name.toUpperCase();
      if (/^[A-Z]{3}$/.test(up)) return up;

      // Try exact match
      let iso3 = this.exactNameToIso3FromSource[name];
      if (iso3) return iso3;

      // Try canonical
      const canon = this._canonName(name);
      iso3 = this.nameToIso3FromSource[canon];
      return iso3 || null;
    },
    extendBounds(bounds, geometry) {
      if (!geometry) return;
      if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)));
      } else if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((coord) => bounds.extend(coord))));
      }
    },
  },
  computed: {
    aspectRatioStyle() {
      return this.aspectToCss(this.aspectRatio);
    },
    wrapperMaxHeightStyle() {
      return this.wrapperMaxHeightPx > 0 ? `${this.wrapperMaxHeightPx}px` : null;
    },
  },
  watch: {
    selectedCountriesDict: {
      immediate: true,
      deep: true,
      handler() {
        this.$nextTick(() => this.applyFeatureStateSelection());
      },
    },
    selectedOceans: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.applyFeatureStateSelection());
      },
    },
  },
};
</script>

<style scoped >
.coverage-map { width: 100%; }
.map-wrapper { width: 100%; display: block; min-height: 200px; }
.maplibre-container { width: 100%; height: 100%; background: #f8f8f8; }
.globe-notice {
  position: absolute;
  left: 12px;
  bottom: 12px;
  max-width: 420px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.4;
  color: #3a3a3a;
  background: #fff8db;
  border: 1px solid #e6d89b;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.legend {
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 6px 8px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
}
.legend-row { display: flex; align-items: center; gap: 6px; }
.legend-row + .legend-row { margin-top: 4px; }
.swatch { display: inline-block; width: 12px; height: 12px; border-radius: 2px; border: 1px solid rgba(0,0,0,0.15); }
.swatch-country { background: #1e90ff; }
.swatch-ocean { background: #1e90ff; }

/* MapLibre Base Styles */
:deep(.maplibregl-map) {
  font: 12px/20px Helvetica Neue,Arial,Helvetica,sans-serif;
  overflow: hidden;
  position: relative;
  -webkit-tap-highlight-color: rgba(0,0,0,0);
}
:deep(.maplibregl-canvas) {
  left: 0;
  position: absolute;
  top: 0;
}
:deep(.maplibregl-ctrl-attrib) {
  background-color: hsla(0,0%,100%,.5);
  margin: 0;
  padding: 0 5px;
  font-size: 12px;
}
:deep(.maplibregl-ctrl-attrib a) {
  color: rgba(0,0,0,.75);
  text-decoration: none;
}
:deep(.maplibregl-ctrl-bottom-right) {
  bottom: 0;
  right: 0;
  position: absolute;
  pointer-events: none;
  z-index: 2;
}
:deep(.maplibregl-ctrl-bottom-right .maplibregl-ctrl) {
  float: right;
  margin: 0 10px 10px 0;
  pointer-events: auto;
}

/* MapLibre Popup Styles */
:deep(.maplibregl-popup) { display: flex; left: 0; pointer-events: none; position: absolute; top: 0; will-change: transform; }
:deep(.maplibregl-popup-anchor-top), :deep(.maplibregl-popup-anchor-top-left), :deep(.maplibregl-popup-anchor-top-right) { flex-direction: column; }
:deep(.maplibregl-popup-anchor-bottom), :deep(.maplibregl-popup-anchor-bottom-left), :deep(.maplibregl-popup-anchor-bottom-right) { flex-direction: column-reverse; }
:deep(.maplibregl-popup-anchor-left) { flex-direction: row; }
:deep(.maplibregl-popup-anchor-right) { flex-direction: row-reverse; }
:deep(.maplibregl-popup-tip) { border: 10px solid transparent; height: 0; width: 0; z-index: 1; }
:deep(.maplibregl-popup-anchor-top .maplibregl-popup-tip) { align-self: center; border-bottom-color: #fff; border-top: none; }
:deep(.maplibregl-popup-anchor-top-left .maplibregl-popup-tip) { align-self: flex-start; border-bottom-color: #fff; border-left: none; border-top: none; }
:deep(.maplibregl-popup-anchor-top-right .maplibregl-popup-tip) { align-self: flex-end; border-bottom-color: #fff; border-right: none; border-top: none; }
:deep(.maplibregl-popup-anchor-bottom .maplibregl-popup-tip) { align-self: center; border-bottom: none; border-top-color: #fff; }
:deep(.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip) { align-self: flex-start; border-bottom: none; border-left: none; border-top-color: #fff; }
:deep(.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip) { align-self: flex-end; border-bottom: none; border-right: none; border-top-color: #fff; }
:deep(.maplibregl-popup-anchor-left .maplibregl-popup-tip) { align-self: center; border-left: none; border-right-color: #fff; }
:deep(.maplibregl-popup-anchor-right .maplibregl-popup-tip) { align-self: center; border-left-color: #fff; border-right: none; }
:deep(.maplibregl-popup-close-button) { background-color: transparent; border: 0; border-radius: 0 3px 0 0; cursor: pointer; position: absolute; right: 0; top: 0; }
:deep(.maplibregl-popup-close-button:hover) { background-color: rgba(0,0,0,.05); }
:deep(.maplibregl-popup-content) { background: #fff; border-radius: 3px; box-shadow: 0 1px 2px rgba(0,0,0,.1); padding: 15px 10px; pointer-events: auto; position: relative; }

/* MapLibre Control Structure Styles (Missing in prod) */
:deep(.maplibregl-ctrl-top-right) {
  top: 0;
  right: 0;
  position: absolute;
  pointer-events: none;
  z-index: 2;
}
:deep(.maplibregl-ctrl-top-right .maplibregl-ctrl) {
  float: right;
  margin: 10px 10px 0 0;
  pointer-events: auto;
  clear: both; /* Force vertical stacking */
}
:deep(.maplibregl-ctrl-group) {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
  overflow: hidden;
}
:deep(.maplibregl-ctrl-group button) {
  width: 29px;
  height: 29px;
  display: block;
  padding: 0;
  outline: none;
  border: 0;
  box-sizing: border-box;
  background-color: transparent;
  cursor: pointer;
}
:deep(.maplibregl-ctrl-group button + button) {
  border-top: 1px solid #ddd;
}

/* Override MapLibre controls to use FontAwesome */
:deep(.maplibregl-ctrl-icon) {
  background-image: none !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}
:deep(.maplibregl-ctrl-icon::before) {
  font-family: "Font Awesome 5 Free", "FontAwesome", sans-serif;
  font-weight: 900;
  font-size: 16px;
  color: #333;
}
:deep(.maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon::before) {
  content: "\f067"; /* fa-plus */
}
:deep(.maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon::before) {
  content: "\f068"; /* fa-minus */
}
:deep(.maplibregl-ctrl-compass .maplibregl-ctrl-icon::before) {
  content: "\f14e"; /* fa-compass */
}
:deep(.maplibregl-ctrl-globe .maplibregl-ctrl-icon::before),
:deep(.maplibregl-ctrl-globe-enabled .maplibregl-ctrl-icon::before) {
  content: "\f0ac"; /* fa-globe */
}
:deep(.maplibregl-ctrl-globe-enabled .maplibregl-ctrl-icon::before) {
  color: #1e90ff;
}

/* Attribution Control Icons & Structure */
:deep(.maplibregl-ctrl-attrib.maplibregl-compact) {
  padding: 2px 2px 2px 2px;
  margin: 10px;
  position: relative;
  background-color: #fff;
  border-radius: 12px;
  box-sizing: content-box;
}
:deep(.maplibregl-ctrl-attrib.maplibregl-compact .maplibregl-ctrl-attrib-inner) {
  display: none;
}
:deep(.maplibregl-ctrl-attrib.maplibregl-compact-show) {
  padding: 2px 2px 2px 2px;
  visibility: visible;
}
:deep(.maplibregl-ctrl-attrib.maplibregl-compact-show .maplibregl-ctrl-attrib-inner) {
  display: block;
}
:deep(.maplibregl-ctrl-attrib-button) {
  background-image: none !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
}
:deep(.maplibregl-ctrl-attrib-button::before) {
  font-family: "Font Awesome 5 Free", "FontAwesome", sans-serif;
  font-weight: 900;
  content: "\f05a"; /* fa-info-circle */
  font-size: 16px;
  color: #333;
}
</style>